import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { list_pages, get_page_documents, create_document, update_document, delete_document, get_document, get_document_file } from '../../api/functions';
import { useApi } from '../../api/api';
import { LoadingSpinner, AdminEndpoint } from '../../components';
import './TabContent.css';

const DocumentsTab = () => {
  const { t, i18n } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const id = params.id;
  const listPagesApi = useApi(list_pages);
  const getPageDocumentsApi = useApi(get_page_documents);
  const createDocumentApi = useApi(create_document);
  const updateDocumentApi = useApi(update_document);
  const deleteDocumentApi = useApi(delete_document);
  const getDocumentApi = useApi(get_document);
  const getDocumentFileApi = useApi(get_document_file);
  const gridRef = useRef(null);

  const [editingDocument, setEditingDocument] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copyFiles, setCopyFiles] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [allDocuments, setAllDocuments] = useState([]);
  const [allPages, setAllPages] = useState([]);
  const [loadedPagesCount, setLoadedPagesCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(16);

  const PAGE_BATCH_SIZE = 50;
  const ROWS_PER_PAGE = 4;

  const fetchDocuments = useCallback(async () => {
    const pagesData = await listPagesApi.execute({
      skip: 0,
      limit: PAGE_BATCH_SIZE,
      show_private: true
    });

    if (pagesData) {
      setAllPages(pagesData);
      setLoadedPagesCount(pagesData.length);
      setHasMorePages(pagesData.length === PAGE_BATCH_SIZE);

      const documentsPromises = pagesData.map(page =>
        getPageDocumentsApi.execute({ page_id: page.id }).catch(() => [])
      );

      const documentsArrays = await Promise.all(documentsPromises);
      const allDocs = documentsArrays.flat();

      const filteredDocs = searchQuery
        ? allDocs.filter(doc => {
          const title = getLocalizedField(doc, 'title').toLowerCase();
          return title.includes(searchQuery.toLowerCase());
        })
        : allDocs;

      setAllDocuments(filteredDocs);
      setCurrentPage(1);
    }
  }, [searchQuery]);

  const loadMorePages = useCallback(async () => {
    if (!hasMorePages || listPagesApi.loading) return;

    const pagesData = await listPagesApi.execute({
      skip: loadedPagesCount,
      limit: PAGE_BATCH_SIZE,
      show_private: true
    });

    if (pagesData && pagesData.length > 0) {
      const newPages = [...allPages, ...pagesData];
      setAllPages(newPages);
      setLoadedPagesCount(loadedPagesCount + pagesData.length);
      setHasMorePages(pagesData.length === PAGE_BATCH_SIZE);

      const documentsPromises = pagesData.map(page =>
        getPageDocumentsApi.execute({ page_id: page.id }).catch(() => [])
      );

      const documentsArrays = await Promise.all(documentsPromises);
      const newDocs = documentsArrays.flat();

      setAllDocuments([...allDocuments, ...newDocs]);
    } else {
      setHasMorePages(false);
    }
  }, [allPages, allDocuments, loadedPagesCount, hasMorePages, listPagesApi.loading]);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new') || currentPath.includes('/new/');
    const isEditingPath = currentPath.includes('/edit');

    if (!isCreatingPath && !isEditingPath) {
      fetchDocuments();
    }
  }, [fetchDocuments]);

  useEffect(() => {
    const calculateColumns = () => {
      if (gridRef.current) {
        const containerWidth = gridRef.current.offsetWidth;
        const minCardWidth = 280;
        const gap = 24;
        const columns = Math.max(1, Math.floor((containerWidth + gap) / (minCardWidth + gap)));
        const newItemsPerPage = ROWS_PER_PAGE * columns;
        setItemsPerPage(newItemsPerPage);
      }
    };

    calculateColumns();
    window.addEventListener('resize', calculateColumns);
    return () => window.removeEventListener('resize', calculateColumns);
  }, []);

  useEffect(() => {
    const currentPath = window.location.pathname;
    const isCreatingPath = currentPath.endsWith('/new');

    if (isCreatingPath) {
      setIsCreating(true);
      setEditingDocument(null);
    } else if (id && currentPath.includes('/edit')) {
      setCopyFiles({});
      const fetchDocumentForEdit = async () => {
        const documentItem = await getDocumentApi.execute({ document_id: parseInt(id) });
        if (documentItem) {
          setEditingDocument(documentItem);
          setIsCreating(false);
        }
      };
      fetchDocumentForEdit();
    } else {
      setIsCreating(false);
      setEditingDocument(null);
      setCopyFiles({});
    }
  }, [id, params]);

  useEffect(() => {
    const neededDocuments = currentPage * itemsPerPage;
    if (neededDocuments > allDocuments.length && hasMorePages && !listPagesApi.loading) {
      loadMorePages();
    }
  }, [currentPage, itemsPerPage, allDocuments.length, hasMorePages, listPagesApi.loading, loadMorePages]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const displayedDocuments = allDocuments.slice(startIndex, endIndex);
  const totalPages = Math.ceil(allDocuments.length / itemsPerPage);

  const handleCreate = () => {
    navigate('/admin/documents/new');
  };

  const handleEdit = (documentItem) => {
    navigate(`/admin/documents/${documentItem.id}/edit`);
  };

  const handleCancel = () => {
    navigate('/admin/documents');
  };

  const storageKey = isCreating ? 'admin_documents_create' : (id ? `admin_documents_edit_${id}` : null);

  const handleCopy = async (documentItem) => {
    const copyData = {
      title_en: documentItem.title_en || '',
      title_cz: documentItem.title_cz || '',
      title_de: documentItem.title_de || '',
      private: documentItem.private || false,
    };

    let copiedFile = null;
    if (documentItem.document_filename) {
      const blob = await getDocumentFileApi.execute({ document_id: documentItem.id });
      if (blob) {
        copiedFile = new File([blob], documentItem.document_filename, { type: blob.type });
      }
    }
    setCopyFiles(copiedFile ? { document: copiedFile } : {});

    localStorage.setItem('admin_documents_create', JSON.stringify(copyData));
    navigate('/admin/documents/new');
  };

  const handleSubmit = async (formData) => {
    if (isCreating) {
      const result = await createDocumentApi.execute({ ...formData });
      if (createDocumentApi.error) {
        toast.error(createDocumentApi.error || 'An error occurred while creating the document', { position: "bottom-right" });
        return;
      }
      if (result) {
        toast.success('Document created successfully!', { position: "bottom-right" });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchDocuments();
        navigate('/admin/documents');
      }
    } else if (editingDocument) {
      const result = await updateDocumentApi.execute({ ...formData, document_id: editingDocument.id });
      if (updateDocumentApi.error) {
        toast.error(updateDocumentApi.error || 'An error occurred while updating the document', { position: "bottom-right" });
        return;
      }
      if (result) {
        toast.success('Document updated successfully!', { position: "bottom-right" });
        if (storageKey) localStorage.removeItem(storageKey);
        await fetchDocuments();
        navigate('/admin/documents');
      }
    }
  };

  const handleDelete = async (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      const result = await deleteDocumentApi.execute({ document_id: documentId });
      if (deleteDocumentApi.error) {
        toast.error(deleteDocumentApi.error || 'An error occurred while deleting the document', {
          position: "bottom-right"
        });
        return;
      }

      if (result) {
        toast.success('Document deleted successfully!', {
          position: "bottom-right"
        });
        await fetchDocuments();
        navigate('/admin/documents');
      }
    }
  };

  const getLocalizedField = (item, fieldPrefix) => {
    const language = i18n.language;
    if (item[`${fieldPrefix}_${language}`]) {
      return item[`${fieldPrefix}_${language}`];
    }
    if (item[`${fieldPrefix}_cz`]) {
      return item[`${fieldPrefix}_cz`];
    }
    if (item[`${fieldPrefix}_en`]) {
      return item[`${fieldPrefix}_en`];
    }
    if (item[`${fieldPrefix}_de`]) {
      return item[`${fieldPrefix}_de`];
    }
    return '';
  };

  const getPageName = (pageId) => {
    const page = allPages.find(p => p.id === pageId);
    if (!page) return `Page #${pageId}`;
    return getLocalizedField(page, 'title') || `Page #${pageId}`;
  };

  const pageOptions = allPages.map(page => ({
    value: page.id,
    label: `#${page.id} - ${getLocalizedField(page, 'title') || 'Untitled Page'}`
  }));

  const documentFields = [
    {
      type: 'language_tabs',
      name: 'content_tabs',
      languages: ['en', 'cz', 'de'],
      fields: [
        { name: 'title', label: t('admin.fields.title'), type: 'text', required: false, placeholder: t('admin.placeholders.documentTitle') }
      ]
    },
    {
      name: 'page_id',
      label: t('admin.fields.page'),
      type: 'select',
      required: true,
      options: pageOptions,
      className: 'page-select-field'
    },
    { name: 'private', label: t('admin.fields.membersOnly'), type: 'boolean', defaultValue: false },
    { name: 'document', label: t('admin.fields.documentFile'), type: 'file', accept: '.pdf,.doc,.docx,.txt,.xlsx,.xls', currentFile: editingDocument?.document_filename || (isCreating ? copyFiles.document?.name : undefined) }
  ];

  if (isCreating || editingDocument) {
    return (
      <div className="tab-container">
        <div className="tab-header">
          <h2 className="tab-title">{isCreating ? t('admin.documents.createTitle') : t('admin.documents.editTitle')}</h2>
          <p className="tab-description">
            {isCreating ? t('admin.documents.createDesc') : `${t('admin.documents.editDesc')}: ${getLocalizedField(editingDocument, 'title')}`}
          </p>
        </div>

        <div className="edit-actions-top">
          <button className="cancel-btn-top" onClick={handleCancel}>
            {t('common.cancel')}
          </button>
          {editingDocument && (
            <button className="copy-btn-top" onClick={() => handleCopy(editingDocument)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              {t('common.copy', 'Copy')}
            </button>
          )}
          {editingDocument && (
            <button
              className="delete-btn"
              onClick={() => handleDelete(editingDocument.id)}
              disabled={deleteDocumentApi.loading}
            >
              {deleteDocumentApi.loading ? t('common.deleting') : t('admin.documents.delete')}
            </button>
          )}
        </div>

        <AdminEndpoint
          fields={documentFields}
          onSubmit={handleSubmit}
          onCancel={null}
          initialData={isCreating ? { ...copyFiles } : (editingDocument || {})}
          submitLabel={isCreating ? t('admin.documents.createBtn') : t('admin.documents.updateBtn')}
          isLoading={createDocumentApi.loading || updateDocumentApi.loading}
          storageKey={storageKey}
        />
      </div>
    );
  }

  return (
    <div className="tab-container">
      <div className="tab-header">
        <div>
          <h2 className="tab-title">{t('admin.documents.title')}</h2>
        </div>
        <button className="create-btn" onClick={handleCreate}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t('admin.documents.createBtn')}
        </button>
      </div>

      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder={t('admin.documents.searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {(listPagesApi.loading || getPageDocumentsApi.loading) && allDocuments.length === 0 ? (
        <LoadingSpinner message={t('common.loading', 'Loading documents...')} />
      ) : allDocuments.length === 0 ? (
        <div className="empty-state">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          <p>{t('admin.documents.noDocuments')}</p>
          <button className="create-btn-secondary" onClick={handleCreate}>
            {t('admin.documents.createBtn')}
          </button>
        </div>
      ) : (
        <>
          <div className="items-grid" ref={gridRef} style={{ position: 'relative', minHeight: (listPagesApi.loading || getPageDocumentsApi.loading) ? '400px' : 'auto' }}>
            {(listPagesApi.loading || getPageDocumentsApi.loading) && allDocuments.length > 0 && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10,
                borderRadius: '12px'
              }}>
                <LoadingSpinner message={t('common.loading', 'Loading...')} />
              </div>
            )}
            {displayedDocuments.map(documentItem => (
              <div key={documentItem.id} className="item-card" onClick={() => handleEdit(documentItem)}>
                <div className="item-card-body">
                  <div className="item-id">#{documentItem.id}</div>
                  <h3 className="item-title">{getLocalizedField(documentItem, 'title') || documentItem.document_filename || 'Untitled Document'}</h3>
                  <div className="item-meta">
                    <span className="item-date">
                      {documentItem.page_id ? getPageName(documentItem.page_id) : '—'}
                    </span>
                    {documentItem.private && <span className="item-badge">Members Only</span>}
                  </div>
                </div>
                <div className="item-card-actions" onClick={(e) => e.stopPropagation()}>
                  <button className="item-copy-btn" onClick={() => handleCopy(documentItem)} title="Duplicate">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                    </svg>
                    Copy
                  </button>
                  <button className="item-delete-btn" onClick={() => handleDelete(documentItem.id)} title="Delete">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline strokeLinecap="round" strokeLinejoin="round" points="3 6 5 6 21 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 11v6M14 11v6" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </button>
              <span className="pagination-info">
                {t('common.page')} {currentPage} {t('common.of')} {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentsTab;
